/**
 * Действия DevOps из карточки задачи: поднять и погасить фича-стенд,
 * переключить рантайм-флаг, собрать релиз.
 *
 * Трекер своих пайплайнов не заводит — он дёргает `play` у джоб ветки
 * (deploy-feature и stop-feature). Поэтому ручки принимают репозиторий и
 * ветку, а не «окружение» как сущность: окружение — это результат прогона, а
 * не запись в базе трекера.
 */
import { devopsApi } from '@/api'

import type { V1DevOpsAction, V1RuntimeFlag } from '@/api/clients/tracker-gitlab'

/** Среда рантайм-флага. Других у инфраструктуры нет. */
export type FlagEnvironment = 'dev' | 'prod'

export const FLAG_ENVIRONMENTS: FlagEnvironment[] = ['dev', 'prod']

/**
 * Флаг в одной среде. Сервис хранит запись на пару «ключ и среда»: один и тот
 * же флаг на dev и prod — это две записи, и включены они бывают по-разному.
 */
export interface RuntimeFlag {
  key: string
  description: string
  environment: FlagEnvironment
  enabled: boolean
  updatedBy: string
  updatedAt: string
}

/** Флаг обеими средами сразу — как его показывает таблица макета */
export interface FlagRow {
  key: string
  description: string
  enabled: Record<FlagEnvironment, boolean>
  updatedBy: string
  updatedAt: string
}

/** Действие DevOps в журнале: кто, что и чем закончилось */
export interface DevOpsAction {
  id: string
  issueId: string
  issueKey: string
  issueTitle: string
  type: string
  projectPath: string
  target: string
  /** Параметры вызова строкой JSON: среда и значение флага, ветка релиза */
  paramsJson: string
  status: string
  actor: string
  pipelineUrl: string
  error: string
  createdAt: string
  finishedAt: string
}

/** Типы записей журнала — те же строки, что отдаёт сервис */
export const ACTION_TYPES = {
  standUp: 'DEVOPS_ACTION_TYPE_FEATURE_ENV_CREATE',
  standDown: 'DEVOPS_ACTION_TYPE_FEATURE_ENV_DELETE',
  flagSet: 'DEVOPS_ACTION_TYPE_RUNTIME_FLAG_SET',
  release: 'DEVOPS_ACTION_TYPE_RELEASE_CREATE',
} as const

export const ACTION_STATUS = {
  pending: 'DEVOPS_ACTION_STATUS_PENDING',
  success: 'DEVOPS_ACTION_STATUS_SUCCESS',
  failed: 'DEVOPS_ACTION_STATUS_FAILED',
} as const

/**
 * Стенд для таблицы раздела: своей сущности у окружения нет, есть «подняли» и
 * «погасили» в журнале. Живой — тот, что подняли и после этого не убрали.
 */
export interface Stand {
  id: string
  projectPath: string
  branch: string
  issueId: string
  issueKey: string
  issueTitle: string
  actor: string
  status: string
  pipelineUrl: string
  raisedAt: string
}

/** Переключение флага из журнала — строка блока «последние переключения» */
export interface FlagChange {
  id: string
  key: string
  environment: FlagEnvironment
  enabled: boolean
  actor: string
  at: string
}

/** Релиз из журнала: тег сервиса и чем закончилась его постановка */
export interface Release {
  id: string
  projectPath: string
  tag: string
  actor: string
  status: string
  pipelineUrl: string
  issueKey: string
  at: string
}

const flagOf = (flag: V1RuntimeFlag): RuntimeFlag => ({
  key: flag.key ?? '',
  description: flag.description ?? '',
  environment: flag.environment === 'prod' ? 'prod' : 'dev',
  enabled: flag.enabled ?? false,
  updatedBy: flag.updatedBy ?? '',
  updatedAt: flag.updatedAt ?? '',
})

const actionOf = (action: V1DevOpsAction): DevOpsAction => ({
  id: action.id ?? '',
  issueId: action.issueId ?? '',
  issueKey: action.issueKey ?? '',
  issueTitle: action.issueTitle ?? '',
  type: String(action.actionType ?? ''),
  projectPath: action.gitlabProjectPath ?? '',
  target: action.target ?? '',
  paramsJson: action.paramsJson ?? '',
  status: String(action.status ?? ''),
  actor: action.actorName ?? '',
  pipelineUrl: action.pipelineUrl ?? '',
  error: action.error ?? '',
  createdAt: action.createdAt ?? '',
  finishedAt: action.finishedAt ?? '',
})

/** Поднять фича-стенд с ветки задачи */
export const raiseStand = async (
  issueId: string,
  projectPath: string,
  branch: string,
): Promise<void> => {
  await devopsApi.devOpsServiceCreateFeatureEnvironment({
    issueId,
    gitlabProjectPath: projectPath,
    branch,
  })
}

/** Погасить фича-стенд */
export const dropStand = async (projectPath: string, branch: string): Promise<void> => {
  await devopsApi.devOpsServiceDeleteFeatureEnvironment({
    gitlabProjectPath: projectPath,
    branch,
  })
}

export const listFlags = async (environment?: FlagEnvironment): Promise<RuntimeFlag[]> => {
  const { data } = await devopsApi.devOpsServiceListRuntimeFlags(environment)

  return (data.flags ?? []).map(flagOf)
}

/**
 * Флаги строками таблицы: записи одного ключа из разных сред сходятся в одну
 * строку с парой тумблеров, как в макете.
 */
export const toFlagRows = (flags: RuntimeFlag[]): FlagRow[] => {
  const rows = new Map<string, FlagRow>()

  for (const flag of flags) {
    const row = rows.get(flag.key) ?? {
      key: flag.key,
      description: flag.description,
      enabled: { dev: false, prod: false },
      updatedBy: flag.updatedBy,
      updatedAt: flag.updatedAt,
    }

    row.enabled[flag.environment] = flag.enabled
    // Показываем самую свежую правку из обеих сред: строка одна, и «когда
    // трогали последний раз» у неё тоже одно.
    if (flag.updatedAt > row.updatedAt) {
      row.updatedAt = flag.updatedAt
      row.updatedBy = flag.updatedBy
    }

    if (!row.description) row.description = flag.description

    rows.set(flag.key, row)
  }

  return [...rows.values()]
}

export const setFlag = async (
  key: string,
  environment: FlagEnvironment,
  enabled: boolean,
): Promise<void> => {
  await devopsApi.devOpsServiceSetRuntimeFlag(key, { environment, enabled })
}

/**
 * До каких репозиториев дошли задачи эпика. Сервер отвечает подсказкой, а
 * решает человек: собирать релиз или нет, видно только ему.
 */
export const listReleaseServices = async (epicId: string): Promise<string[]> => {
  const { data } = await devopsApi.devOpsServiceListReleaseServices(epicId)

  return data.gitlabProjectPaths ?? []
}

export const createRelease = async (
  projectPath: string,
  tag: string,
  ref: string,
  description: string,
  issueId?: string,
): Promise<void> => {
  await devopsApi.devOpsServiceCreateRelease({
    gitlabProjectPath: projectPath,
    tag,
    ref,
    description,
    issueId,
  })
}

/**
 * Журнал действий: чем закончился каждый запуск. Без задачи в запросе это
 * сквозной журнал раздела — сервис сам сужает его до проектов, куда
 * запросивший допущен.
 */
export const listActions = async (issueId?: string, perPage = 100): Promise<DevOpsAction[]> => {
  const { data } = await devopsApi.devOpsServiceListDevOpsActions(issueId, '1', String(perPage))

  return (data.actions ?? []).map(actionOf)
}

/** Ключ стенда: одна ветка одного репозитория — одно окружение. */
const standKey = (action: DevOpsAction) => `${action.projectPath}::${action.target}`

/**
 * Живые стенды из журнала, свежие первыми.
 *
 * Журнал приходит от новых к старым, поэтому первая встреченная запись по
 * ключу и есть текущее состояние: если это «погасили» — стенда нет, если
 * «подняли» — есть. Неудачную попытку подъёма стендом не считаем: окружения
 * после неё нет, а строка «активен» звала бы ходить по мёртвой ссылке.
 */
export const toStands = (actions: DevOpsAction[]): Stand[] => {
  const seen = new Set<string>()
  const stands: Stand[] = []

  for (const action of actions) {
    if (action.type !== ACTION_TYPES.standUp && action.type !== ACTION_TYPES.standDown) continue

    const key = standKey(action)

    if (seen.has(key)) continue

    seen.add(key)

    if (action.type === ACTION_TYPES.standDown) continue
    if (action.status === ACTION_STATUS.failed) continue

    stands.push({
      id: action.id,
      projectPath: action.projectPath,
      branch: action.target,
      issueId: action.issueId,
      issueKey: action.issueKey,
      issueTitle: action.issueTitle,
      actor: action.actor,
      status: action.status,
      pipelineUrl: action.pipelineUrl,
      raisedAt: action.createdAt,
    })
  }

  return stands
}

/** Переключения флагов из журнала: среда и значение лежат в параметрах вызова. */
export const toFlagChanges = (actions: DevOpsAction[]): FlagChange[] =>
  actions
    .filter((action) => action.type === ACTION_TYPES.flagSet)
    .map((action) => {
      const params = parseParams(action)

      return {
        id: action.id,
        key: action.target,
        environment: params.environment === 'prod' ? ('prod' as const) : ('dev' as const),
        enabled: Boolean(params.enabled),
        actor: action.actor,
        at: action.createdAt,
      }
    })

/**
 * Кто и когда трогал флаг последним — из журнала.
 *
 * Сам файл флагов этого не хранит: в нём пара «ключ — включён». Журнал знает,
 * потому что переключают флаг через трекер; правку, сделанную коммитом мимо
 * него, колонка законно не покажет.
 */
export const withLastChange = (rows: FlagRow[], changes: FlagChange[]): FlagRow[] => {
  const latest = new Map<string, FlagChange>()

  // Журнал идёт от свежих к старым — первая встреченная запись и есть последняя.
  for (const change of changes) {
    if (!latest.has(change.key)) latest.set(change.key, change)
  }

  return rows.map((row) => {
    const change = latest.get(row.key)

    return change ? { ...row, updatedBy: change.actor, updatedAt: change.at } : row
  })
}

/** Релизы из журнала, свежие первыми */
export const toReleases = (actions: DevOpsAction[]): Release[] =>
  actions
    .filter((action) => action.type === ACTION_TYPES.release)
    .map((action) => ({
      id: action.id,
      projectPath: action.projectPath,
      tag: action.target,
      actor: action.actor,
      status: action.status,
      pipelineUrl: action.pipelineUrl,
      issueKey: action.issueKey,
      at: action.createdAt,
    }))

/**
 * Параметры вызова лежат строкой JSON. Битую строку глотаем: журнал ведёт
 * сервис, и ради одной непрочитанной записи весь блок ронять незачем.
 */
const parseParams = (action: DevOpsAction): { environment?: string; enabled?: boolean } => {
  try {
    return JSON.parse(action.paramsJson || '{}') as { environment?: string; enabled?: boolean }
  } catch {
    return {}
  }
}
